export interface InputState {
  moveX: number;
  moveZ: number;
  confirm: boolean;
  cancel: boolean;
  menu: boolean;
  /** Edge: true only on the frame the action transitioned to pressed. */
  confirmPressed: boolean;
  cancelPressed: boolean;
  menuPressed: boolean;
}

const STICK_RADIUS_PX = 80;
const STICK_DEADZONE = 0.12;
const GAMEPAD_DEADZONE = 0.15;
const TAP_MAX_MS = 280;
const TAP_MAX_PX = 12;

const STANDARD_CONFIRM_BUTTON = 0;
const STANDARD_CANCEL_BUTTON = 1;
const STANDARD_MENU_BUTTON = 9;

const newState = (): InputState => ({
  moveX: 0,
  moveZ: 0,
  confirm: false,
  cancel: false,
  menu: false,
  confirmPressed: false,
  cancelPressed: false,
  menuPressed: false,
});

export class InputController {
  readonly state: InputState = newState();

  private readonly keys = new Set<string>();

  private touching = false;
  private activePointerId = -1;
  private originX = 0;
  private originY = 0;
  private touchMoveX = 0;
  private touchMoveZ = 0;
  private touchStartMs = 0;
  private touchDragged = false;
  private tapConfirmPending = false;

  private gamepadMoveX = 0;
  private gamepadMoveZ = 0;
  private gamepadConfirm = false;
  private gamepadCancel = false;
  private gamepadMenu = false;

  private prevConfirm = false;
  private prevCancel = false;
  private prevMenu = false;

  private detach: (() => void) | null = null;

  attach(canvas: HTMLElement): () => void {
    if (this.detach !== null) throw new Error('InputController already attached');

    const onKeyDown = (e: KeyboardEvent): void => {
      this.keys.add(e.code);
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      this.keys.delete(e.code);
    };
    const onBlur = (): void => {
      this.keys.clear();
      this.touching = false;
      this.activePointerId = -1;
      this.touchMoveX = 0;
      this.touchMoveZ = 0;
    };
    const onPointerDown = (e: PointerEvent): void => {
      if (this.touching) return;
      this.touching = true;
      this.activePointerId = e.pointerId;
      this.originX = e.clientX;
      this.originY = e.clientY;
      this.touchMoveX = 0;
      this.touchMoveZ = 0;
      this.touchStartMs = performance.now();
      this.touchDragged = false;
      canvas.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: PointerEvent): void => {
      if (!this.touching || e.pointerId !== this.activePointerId) return;
      const dx = e.clientX - this.originX;
      const dy = e.clientY - this.originY;
      const len = Math.hypot(dx, dy);
      if (len > TAP_MAX_PX) this.touchDragged = true;
      const norm = len > 0 ? Math.min(len, STICK_RADIUS_PX) / STICK_RADIUS_PX : 0;
      if (norm < STICK_DEADZONE) {
        this.touchMoveX = 0;
        this.touchMoveZ = 0;
      } else {
        const scale = norm / len;
        this.touchMoveX = dx * scale;
        this.touchMoveZ = dy * scale;
      }
    };
    const onPointerEnd = (e: PointerEvent): void => {
      if (e.pointerId !== this.activePointerId) return;
      if (!this.touchDragged && performance.now() - this.touchStartMs < TAP_MAX_MS) {
        this.tapConfirmPending = true;
      }
      this.touching = false;
      this.activePointerId = -1;
      this.touchMoveX = 0;
      this.touchMoveZ = 0;
      this.touchDragged = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerEnd);
    canvas.addEventListener('pointercancel', onPointerEnd);

    this.detach = (): void => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerEnd);
      canvas.removeEventListener('pointercancel', onPointerEnd);
      this.detach = null;
    };
    return this.detach;
  }

  poll(): void {
    this.pollGamepad();
    this.composeState();
  }

  private pollGamepad(): void {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
    const pads = navigator.getGamepads();
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p !== null && p !== undefined && p.connected) {
        gp = p;
        break;
      }
    }
    if (gp === null) {
      this.gamepadMoveX = 0;
      this.gamepadMoveZ = 0;
      this.gamepadConfirm = false;
      this.gamepadCancel = false;
      this.gamepadMenu = false;
      return;
    }
    const lx = gp.axes[0] ?? 0;
    const ly = gp.axes[1] ?? 0;
    this.gamepadMoveX = Math.abs(lx) > GAMEPAD_DEADZONE ? lx : 0;
    this.gamepadMoveZ = Math.abs(ly) > GAMEPAD_DEADZONE ? ly : 0;
    this.gamepadConfirm = gp.buttons[STANDARD_CONFIRM_BUTTON]?.pressed ?? false;
    this.gamepadCancel = gp.buttons[STANDARD_CANCEL_BUTTON]?.pressed ?? false;
    this.gamepadMenu = gp.buttons[STANDARD_MENU_BUTTON]?.pressed ?? false;
  }

  private composeState(): void {
    const keyLeft = this.keys.has('KeyA') || this.keys.has('ArrowLeft');
    const keyRight = this.keys.has('KeyD') || this.keys.has('ArrowRight');
    const keyFwd = this.keys.has('KeyW') || this.keys.has('ArrowUp');
    const keyBack = this.keys.has('KeyS') || this.keys.has('ArrowDown');
    const keyMoveX = (keyRight ? 1 : 0) - (keyLeft ? 1 : 0);
    const keyMoveZ = (keyBack ? 1 : 0) - (keyFwd ? 1 : 0);

    let mx = keyMoveX + this.touchMoveX + this.gamepadMoveX;
    let mz = keyMoveZ + this.touchMoveZ + this.gamepadMoveZ;
    const len = Math.hypot(mx, mz);
    if (len > 1) {
      mx /= len;
      mz /= len;
    }
    this.state.moveX = mx;
    this.state.moveZ = mz;

    const keyConfirm = this.keys.has('Enter') || this.keys.has('Space');
    const keyCancel = this.keys.has('Escape');
    const keyMenu = this.keys.has('Tab');

    const tapConfirm = this.tapConfirmPending;
    this.tapConfirmPending = false;

    const confirm = keyConfirm || this.gamepadConfirm || tapConfirm;
    const cancel = keyCancel || this.gamepadCancel;
    const menu = keyMenu || this.gamepadMenu;

    this.state.confirmPressed = confirm && !this.prevConfirm;
    this.state.cancelPressed = cancel && !this.prevCancel;
    this.state.menuPressed = menu && !this.prevMenu;
    this.state.confirm = confirm;
    this.state.cancel = cancel;
    this.state.menu = menu;

    this.prevConfirm = confirm;
    this.prevCancel = cancel;
    this.prevMenu = menu;
  }
}
